import { useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type PropsWithChildren,
} from "react";

type ChannelContextType = {
  code: string;
  onChangeCode: (newCode: string) => Promise<void>;
};

const ChannelContext = createContext<ChannelContextType | null>(null);

export const ChannelProvider = ({
  children,
  code: initialCode,
}: PropsWithChildren<{ code: string }>) => {
  const [code, setCode] = useState(initialCode);
  const queryClient = useQueryClient();

  const onChangeCode = useCallback(
    async (newCode: string) => {
      localStorage.setItem("channelCode", newCode);
      setCode(newCode);
      await queryClient.cancelQueries();
      queryClient.clear();
      setTimeout(() => window.location.reload(), 300);
    },
    [queryClient],
  );

  useEffect(() => {
    const storedChannelCode = localStorage.getItem("channelCode");
    if (storedChannelCode) {
      setCode(storedChannelCode);
    } else if (initialCode) {
      localStorage.setItem("channelCode", initialCode);
      setCode(initialCode);
    }
  }, [initialCode]);

  return (
    <ChannelContext.Provider value={{ code, onChangeCode }}>
      {children}
    </ChannelContext.Provider>
  );
};

export const useChannelContext = () => {
  const context = useContext(ChannelContext);

  if (!context) {
    throw new Error("useChannelContext must be used within a ChannelProvider");
  }

  return context;
};
